import {
  useGetApiUtilitiesCategoriesIdInsights,
  useGetApiSettingsHomeLocation,
  type UtilitiesTreeNode,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Clock,
  AlertTriangle,
  TrendingUp,
  Coins,
  Calendar,
} from 'lucide-react';
import { useMemo, useCallback, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { HistogramChart, Tooltip } from '@/components/ui';

type RangePreset =
  | 'today'
  | 'yesterday'
  | 'wtd'
  | 'mtd'
  | 'ytd'
  | 'lastWeek'
  | 'lastMonth'
  | `${number}d`
  | `${number}w`
  | `${number}M`;
type ChartMode = 'consumption' | 'cost';
type BucketSize = '1h' | '1d' | '1w' | '1M';

const RANGE_OPTIONS: { value: RangePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'mtd', label: 'MTD' },
  { value: 'ytd', label: 'YTD' },
];

// Presets that are valid for the API
const VALID_PRESETS = new Set(['today', 'yesterday', 'wtd', 'mtd', 'ytd', 'lastWeek', 'lastMonth']);

// Check if a preset string is valid (base presets or Nd/Nw/NM patterns)
function isValidPreset(value: string | null): value is RangePreset {
  if (value === null) return false;
  // Check base presets
  if (VALID_PRESETS.has(value)) return true;
  // Check Nd pattern (e.g., "7d", "30d")
  if (/^\d+d$/.test(value)) return true;
  // Check Nw pattern (e.g., "2w", "4w")
  if (/^\d+w$/.test(value)) return true;
  // Check NM pattern (e.g., "3M", "6M")
  if (/^\d+M$/.test(value)) return true;
  return false;
}

function isValidChartMode(value: string | null): value is ChartMode {
  return value === 'consumption' || value === 'cost';
}

// Number formatters
const CONSUMPTION_FORMATTER = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatConsumption(value?: number): string {
  if (value === undefined || value === null) return '—';
  return CONSUMPTION_FORMATTER.format(value);
}

function formatCost(value?: number, currencyCode?: string): string {
  if (value === undefined || value === null) return '—';
  if (currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(value);
    } catch {
      // Fall back to plain number if currency code is invalid
    }
  }
  return value.toFixed(2);
}

function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

// Calculate number of days in a range
function getRangeDays(preset: RangePreset): number {
  switch (preset) {
    case 'today':
      return 1;
    case '7d':
      return 7;
    case '30d':
      return 30;
    case 'mtd': {
      const now = new Date();
      return now.getDate();
    }
    case 'ytd': {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1);
      return Math.ceil((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }
    default:
      return 1;
  }
}

// Get default bucket size for preset (matches backend defaults)
function getDefaultBucket(preset: RangePreset): BucketSize {
  // Today/yesterday: hourly
  if (preset === 'today' || preset === 'yesterday') return '1h';
  // WTD/lastWeek: daily
  if (preset === 'wtd' || preset === 'lastWeek') return '1d';
  // MTD/lastMonth: daily
  if (preset === 'mtd' || preset === 'lastMonth') return '1d';
  // YTD: monthly
  if (preset === 'ytd') return '1M';
  // Nd: hourly for <= 2 days, daily otherwise
  const daysMatch = preset.match(/^(\d+)d$/);
  if (daysMatch && daysMatch[1]) {
    const days = parseInt(daysMatch[1], 10);
    return days <= 2 ? '1h' : '1d';
  }
  // Nw: daily
  if (/^\d+w$/.test(preset)) return '1d';
  // NM: monthly
  if (/^\d+M$/.test(preset)) return '1M';
  // Default
  return '1d';
}

// Get allowed bucket sizes for preset (matches backend rules)
function getAllowedBuckets(preset: RangePreset): BucketSize[] {
  if (preset === 'today' || preset === 'yesterday') return ['1h'];
  if (preset === 'wtd' || preset === 'lastWeek') return ['1h', '1d'];
  if (preset === 'mtd' || preset === 'lastMonth') return ['1d'];
  if (preset === 'ytd') return ['1d', '1w', '1M'];
  // Nd
  const daysMatch = preset.match(/^(\d+)d$/);
  if (daysMatch && daysMatch[1]) {
    const days = parseInt(daysMatch[1], 10);
    return days <= 2 ? ['1h'] : ['1d'];
  }
  // Nw
  if (/^\d+w$/.test(preset)) return ['1d', '1w'];
  // NM
  if (/^\d+M$/.test(preset)) return ['1d', '1w', '1M'];
  return ['1d'];
}

// Tree node component
interface TreeNodeRowProps {
  node: UtilitiesTreeNode;
  parentConsumption?: number;
  unitSymbol?: string;
  level: number;
  defaultExpanded: boolean;
}

function TreeNodeRow({
  node,
  parentConsumption,
  unitSymbol,
  level,
  defaultExpanded,
}: TreeNodeRowProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const hasChildren = (node.children?.length ?? 0) > 0;
  const consumption = node.consumption ?? 0;
  const unaccounted = node.unaccounted ?? 0;
  const isNegativeUnaccounted = node.unaccountedNegative ?? false;
  // Only show unaccounted for nodes that have children (leaf nodes can't have unaccounted)
  const showUnaccounted = hasChildren && (unaccounted !== 0 || isNegativeUnaccounted);

  // Calculate percentage of parent
  const percentage =
    parentConsumption && parentConsumption > 0 ? consumption / parentConsumption : undefined;

  return (
    <>
      <div
        className={`flex items-center gap-2 border-b border-gray-100 py-2.5 dark:border-white/[0.04] ${
          level > 0 ? 'pl-' + level * 6 : ''
        }`}
        style={{ paddingLeft: level * 24 }}
      >
        {/* Expand/collapse button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={!hasChildren}
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors ${
            hasChildren
              ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.06] dark:hover:text-zinc-300'
              : 'invisible'
          }`}
        >
          {hasChildren &&
            (isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            ))}
        </button>

        {/* Name */}
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">
          {node.name || 'Unnamed'}
        </span>

        {/* Percentage of parent */}
        {percentage !== undefined && (
          <span className="shrink-0 text-xs text-gray-500 dark:text-zinc-500">
            {formatPercentage(percentage)}
          </span>
        )}

        {/* Consumption */}
        <span className="shrink-0 text-right text-sm tabular-nums text-gray-900 dark:text-white">
          {formatConsumption(consumption)}{' '}
          <span className="text-gray-500 dark:text-zinc-500">{unitSymbol}</span>
        </span>
      </div>

      {/* Unaccounted row */}
      {showUnaccounted && isExpanded && (
        <div
          className={`flex items-center gap-2 border-b border-gray-100 py-2 dark:border-white/[0.04] ${
            isNegativeUnaccounted
              ? 'bg-red-50 dark:bg-red-500/5'
              : 'bg-gray-50 dark:bg-white/[0.02]'
          }`}
          style={{ paddingLeft: (level + 1) * 24 }}
        >
          <div className="h-6 w-6 shrink-0" /> {/* Spacer for alignment */}
          <span
            className={`flex min-w-0 flex-1 items-center gap-1.5 text-sm italic ${
              isNegativeUnaccounted
                ? 'text-red-600 dark:text-red-400'
                : 'text-gray-500 dark:text-zinc-500'
            }`}
          >
            Unaccounted
            {isNegativeUnaccounted && (
              <Tooltip content="Children exceed parent consumption for this period" position="top">
                <AlertTriangle className="h-3.5 w-3.5 cursor-help" />
              </Tooltip>
            )}
          </span>
          <span
            className={`shrink-0 text-right text-sm tabular-nums ${
              isNegativeUnaccounted
                ? 'font-medium text-red-600 dark:text-red-400'
                : 'text-gray-600 dark:text-zinc-400'
            }`}
          >
            {formatConsumption(unaccounted)}{' '}
            <span className={isNegativeUnaccounted ? '' : 'text-gray-500 dark:text-zinc-500'}>
              {unitSymbol}
            </span>
          </span>
        </div>
      )}

      {/* Children */}
      {isExpanded &&
        hasChildren &&
        node.children?.map((child, index) => (
          <TreeNodeRow
            key={child.id || `child-${index}`}
            node={child}
            parentConsumption={consumption}
            unitSymbol={unitSymbol}
            level={level + 1}
            defaultExpanded={level < 1} // Expand to 2 levels by default
          />
        ))}
    </>
  );
}

export function UtilityCategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse range from URL, defaulting to '30d'
  const range = useMemo((): RangePreset => {
    const rangeParam = searchParams.get('range');
    return isValidPreset(rangeParam) ? rangeParam : '30d';
  }, [searchParams]);

  // Parse chart mode from URL, defaulting to 'consumption'
  const chartMode = useMemo((): ChartMode => {
    const modeParam = searchParams.get('chart');
    return isValidChartMode(modeParam) ? modeParam : 'consumption';
  }, [searchParams]);

  // Get allowed buckets for current preset
  const allowedBuckets = useMemo(() => getAllowedBuckets(range), [range]);

  // Parse bucket from URL, defaulting to preset's default
  const bucket = useMemo((): BucketSize => {
    const bucketParam = searchParams.get('bucket');
    if (bucketParam && allowedBuckets.includes(bucketParam as BucketSize)) {
      return bucketParam as BucketSize;
    }
    return getDefaultBucket(range);
  }, [searchParams, range, allowedBuckets]);

  const handleRangeChange = useCallback(
    (newRange: RangePreset) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('range', newRange);
        // Clear bucket when changing preset (will default to new preset's default)
        next.delete('bucket');
        return next;
      });
    },
    [setSearchParams]
  );

  const handleBucketChange = useCallback(
    (newBucket: BucketSize) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (newBucket === getDefaultBucket(range)) {
          next.delete('bucket');
        } else {
          next.set('bucket', newBucket);
        }
        return next;
      });
    },
    [setSearchParams, range]
  );

  const handleChartModeChange = useCallback(
    (mode: ChartMode) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (mode === 'consumption') {
          next.delete('chart');
        } else {
          next.set('chart', mode);
        }
        return next;
      });
    },
    [setSearchParams]
  );

  // Check timezone
  const {
    data: homeLocationData,
    isLoading: isLoadingHome,
    isError: isHomeError,
  } = useGetApiSettingsHomeLocation();
  const homeLocation = homeLocationData?.location;
  const hasTimezone = Boolean(homeLocation?.timezone && homeLocation.timezone.trim() !== '');

  // Build query params
  const insightsParams = useMemo(() => {
    return {
      preset: range,
      bucket: bucket,
    };
  }, [range, bucket]);

  // Fetch insights data
  const {
    data: insightsData,
    isLoading: isLoadingInsights,
    isFetching: isFetchingInsights,
    isError: isInsightsError,
  } = useGetApiUtilitiesCategoriesIdInsights(categoryId ?? '', insightsParams, {
    query: {
      enabled: !!categoryId && hasTimezone,
      // Keep previous data while fetching new data to prevent layout shift
      placeholderData: (prev) => prev,
    },
  });

  const category = insightsData?.category;
  const totals = insightsData?.totals;
  const series = useMemo(() => insightsData?.series ?? [], [insightsData?.series]);
  const sensorSeries = useMemo(
    () => insightsData?.sensorSeries ?? [],
    [insightsData?.sensorSeries]
  );
  const tree = insightsData?.tree;
  const currencyCode = insightsData?.currencyCode;
  const rangeInfo = insightsData?.range;

  // Initial loading (no data yet) vs refetching (updating with new params)
  const isInitialLoading = isLoadingHome || (isLoadingInsights && !insightsData);
  const isRefetching = isFetchingInsights && !!insightsData;
  const isError = isHomeError || isInsightsError;

  // Calculate average per day for multi-day ranges
  const avgPerDay = useMemo(() => {
    if (!totals?.consumption) return undefined;
    const days = getRangeDays(range);
    if (days <= 1) return undefined;
    return totals.consumption / days;
  }, [totals?.consumption, range]);

  // Prepare chart data - use per-sensor series when available for stacked view
  const chartData = useMemo(() => {
    if (chartMode === 'consumption') {
      return series.map((point) => ({
        timestamp: point.timestamp || '',
        value: point.consumption ?? null,
      }));
    }
    return series.map((point) => ({
      timestamp: point.timestamp || '',
      value: point.cost ?? null,
    }));
  }, [series, chartMode]);

  // Per-sensor datasets for stacked charts
  const chartDatasets = useMemo(() => {
    if (sensorSeries.length === 0) return undefined;

    return sensorSeries.map((sensor) => ({
      label: sensor.sensorName || sensor.sensorHumanId || 'Unknown',
      data: (sensor.data ?? []).map((point) => ({
        timestamp: point.timestamp || '',
        value: chartMode === 'consumption' ? (point.consumption ?? null) : (point.cost ?? null),
      })),
    }));
  }, [sensorSeries, chartMode]);

  // Chart time bounds
  const chartStartTime = rangeInfo?.start || new Date().toISOString();
  const chartEndTime = rangeInfo?.end || new Date().toISOString();

  if (isInitialLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (!hasTimezone) {
    return (
      <div className="space-y-6">
        <Link
          to={ROUTES.UTILITIES}
          className="inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Utilities
        </Link>
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/10">
            <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Set your home timezone
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-zinc-500">
            Configure your home location to use the Utility Dashboard.
          </p>
        </div>
      </div>
    );
  }

  if (isError || !category) {
    return (
      <div className="space-y-6">
        <Link
          to={ROUTES.UTILITIES}
          className="inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Utilities
        </Link>
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
            <AlertCircle className="h-6 w-6 text-red-400" />
          </div>
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            {isError ? 'Failed to load category data' : 'Category not found'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to={`${ROUTES.UTILITIES}?range=${range}`}
        className="inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Utilities
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            {category.name}
          </h1>
          <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
            Consumption and cost breakdown
          </p>
        </div>

        {/* Bucket and range selectors */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Bucket selector - shown first so range selector doesn't jump when it appears */}
          {allowedBuckets.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-zinc-500">Bucket</span>
              <div className="flex rounded-lg bg-gray-100 p-0.5 dark:bg-zinc-800">
                {allowedBuckets.map((b) => (
                  <button
                    key={b}
                    onClick={() => handleBucketChange(b)}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                      bucket === b
                        ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                        : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white'
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-zinc-500">Range</span>
            <div className="flex rounded-lg bg-gray-100 p-0.5 dark:bg-zinc-800">
              {RANGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleRangeChange(option.value)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    range === option.value
                      ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                      : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Key stats */}
      <div
        className={`grid gap-4 transition-opacity duration-200 sm:grid-cols-2 lg:grid-cols-3 ${
          isRefetching ? 'opacity-50' : 'opacity-100'
        }`}
      >
        {/* Total consumption */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
              <TrendingUp className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">
                Total Consumption
              </p>
              <p className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900 dark:text-white">
                {formatConsumption(totals?.consumption)}{' '}
                <span className="text-sm font-normal text-gray-500 dark:text-zinc-500">
                  {category.unitSymbol}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Total cost */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10">
              <Coins className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Total Cost</p>
              <p className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900 dark:text-white">
                {formatCost(totals?.cost, currencyCode)}
              </p>
            </div>
          </div>
        </div>

        {/* Average per day (for multi-day ranges) */}
        {avgPerDay !== undefined && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/[0.06] dark:bg-[#18181b]">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
                <Calendar className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Avg / Day</p>
                <p className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900 dark:text-white">
                  {formatConsumption(avgPerDay)}{' '}
                  <span className="text-sm font-normal text-gray-500 dark:text-zinc-500">
                    {category.unitSymbol}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Root sensor breakdown */}
      {totals?.rootSensors && totals.rootSensors.length > 1 && (
        <div
          className={`rounded-xl border border-gray-200 bg-white transition-opacity duration-200 dark:border-white/[0.06] dark:bg-[#18181b] ${
            isRefetching ? 'opacity-50' : 'opacity-100'
          }`}
        >
          <div className="border-b border-gray-100 px-4 py-3 dark:border-white/[0.04]">
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">Root Sensors</h2>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-white/[0.04]">
            {totals.rootSensors.map((sensor) => (
              <div
                key={sensor.sensorHumanId}
                className="flex items-center justify-between px-4 py-3"
              >
                <span className="text-sm text-gray-900 dark:text-white">
                  {sensor.sensorName || sensor.sensorHumanId}
                </span>
                <div className="flex items-center gap-4">
                  <span className="text-sm tabular-nums text-gray-600 dark:text-zinc-400">
                    {formatConsumption(sensor.consumption)} {category.unitSymbol}
                  </span>
                  <span className="text-sm tabular-nums text-gray-600 dark:text-zinc-400">
                    {formatCost(sensor.cost, currencyCode)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chart */}
      <div
        className={`rounded-xl border border-gray-200 bg-white transition-opacity duration-200 dark:border-white/[0.06] dark:bg-[#18181b] ${
          isRefetching ? 'opacity-50' : 'opacity-100'
        }`}
      >
        {/* Chart controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-4 py-3 dark:border-white/[0.04]">
          {/* Left side: Chart mode toggle */}
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg bg-gray-100 p-0.5 dark:bg-zinc-800">
              <button
                onClick={() => handleChartModeChange('consumption')}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  chartMode === 'consumption'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                    : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white'
                }`}
              >
                Consumption
              </button>
              <button
                onClick={() => handleChartModeChange('cost')}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  chartMode === 'cost'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                    : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white'
                }`}
              >
                Cost
              </button>
            </div>
            {series.length > 0 && (
              <span className="text-xs text-gray-500 dark:text-zinc-500">
                {series.length} buckets
              </span>
            )}
          </div>
        </div>

        {/* Chart area */}
        <div className="h-80 p-4">
          <HistogramChart
            data={chartData}
            datasets={chartDatasets}
            startTime={chartStartTime}
            endTime={chartEndTime}
            unit={chartMode === 'consumption' ? category.unitSymbol : currencyCode}
            bucketSize={bucket}
          />
        </div>
      </div>

      {/* Consumption Tree */}
      {tree && (
        <div
          className={`rounded-xl border border-gray-200 bg-white transition-opacity duration-200 dark:border-white/[0.06] dark:bg-[#18181b] ${
            isRefetching ? 'opacity-50' : 'opacity-100'
          }`}
        >
          <div className="border-b border-gray-100 px-4 py-3 dark:border-white/[0.04]">
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">
              Consumption Breakdown
            </h2>
          </div>
          <div className="px-4">
            <TreeNodeRow
              node={tree}
              unitSymbol={category.unitSymbol}
              level={0}
              defaultExpanded={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
